import React, { useCallback, useContext } from 'react';
import { useConfigurableMessageAndIconMap } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { LinkMenuItem } from '@atlassian/navigation-system/side-nav-items/link-menu-item';
import { ButtonMenuItem } from '@atlassian/navigation-system/side-nav-items/button-menu-item';
import { PeekMenuIdContext } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { convertToConfigurableMenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/convert-to-configurable-menu-id/index.tsx';
import { useSectionItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { SECTION_ITEM_MAP } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { isGlobalAppMenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/global-apps/index.tsx';
import { GLOBAL_APP_MENU_ID_TO_APS_KEY } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { USER_NAVIGATION_ITEM_LINKS } from '../../../../common/constants/index.tsx';

export type MoreMenuItemProps = {
	// eslint-disable-next-line jira/react/handler-naming
	closeMenu: () => void;
	menuId: string;
	key: string;
	getAppShortcutURL?: (apsKey: string) => string | undefined;
};

export function MoreMenuItem({ menuId, closeMenu, key, getAppShortcutURL }: MoreMenuItemProps) {
	const { setPeekMenuId } = useContext(PeekMenuIdContext);

	const configurableId = convertToConfigurableMenuId(menuId);
	const isGlobalApp = isGlobalAppMenuId(menuId);

	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(configurableId);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	// TODO: put under feature gate
	const getProductUrl = useCallback(() => {
		if (!isGlobalApp || !getAppShortcutURL) {
			return;
		}

		if (GLOBAL_APP_MENU_ID_TO_APS_KEY[menuId]) {
			const apsKey = GLOBAL_APP_MENU_ID_TO_APS_KEY[menuId];
			return getAppShortcutURL(apsKey);
		}
	}, [isGlobalApp, getAppShortcutURL, menuId]);

	// This is part of an intermediate state workaround where links are accounted for;
	// we don't expect links to be in the More menu at the end state,
	// at which point this and the MenuLinkItem below can be removed.

	const url = fg('nav4_global_links_jira')
		? getProductUrl() || USER_NAVIGATION_ITEM_LINKS[menuId]
		: USER_NAVIGATION_ITEM_LINKS[menuId];

	const onClick = useCallback(() => {
		closeMenu();

		if (!isGlobalApp) {
			// Global apps open in a new tab onClick so they don't need to be a peek-item
			setPeekMenuId(configurableId);
		}

		sendSectionItemAnalyticsEvent({
			menuId: configurableId,
			sectionItem: SECTION_ITEM_MAP[configurableId],
		});
	}, [closeMenu, configurableId, isGlobalApp, sendSectionItemAnalyticsEvent, setPeekMenuId]);

	return (
		<>
			{url ? (
				<LinkMenuItem
					elemBefore={icon}
					href={url}
					onClick={onClick}
					key={key}
					target={isGlobalApp ? '_blank' : undefined}
				>
					{formattedMessage}
				</LinkMenuItem>
			) : (
				<ButtonMenuItem elemBefore={icon} onClick={onClick} key={key}>
					{formattedMessage}
				</ButtonMenuItem>
			)}
		</>
	);
}
